import { useTheme } from "@mui/material/styles";

import { catClassName } from "./catFocus";
import { CAPTION_FONT_SIZE } from "./constants";
import { CustomizedInjectedProps } from "./rechartsCustomized";

// One hovered value to caption "<label>: <value>" next to its active dot.
export interface CaptionItem {
  // recharts dataKey of the series on the rows.
  dataKey: string;
  label: string;
  // Tags the caption with the cat class so it fades with its cat (hover focus).
  catId?: number;
}

export interface Captions {
  items: CaptionItem[];
  format: (value: number) => string;
}

// Legacy `labelPos`: captions closer than this are pushed apart.
const MIN_SPACING = CAPTION_FONT_SIZE + 2;
// Matches the recharts active dot (r 4) plus a gap.
const DOT_CLEARANCE = 8;

// Spreads sorted y positions at least `min` apart, pushing down from `top`;
// a run that ends past `bottom` is lifted back up as a whole.
const spreadPositions = (
  ys: number[],
  min: number,
  top: number,
  bottom: number
): number[] => {
  const out: number[] = [];
  let last = -Infinity;
  ys.forEach((y) => {
    const next = Math.max(y, last + min, top);
    out.push(next);
    last = next;
  });
  const overflow = last - bottom;
  return overflow > 0 ? out.map((y) => y - overflow) : out;
};

/**
 * Captions of the hovered values, drawn in one pass to the left of the active
 * dots so they can be spread apart when two series come close (the legacy
 * reading, where each dot carried its own text and `labelPos` kept the texts
 * from overlapping). A halo stroke keeps the text legible over the curves and
 * grid. Rendered through recharts <Customized>, which hands it the hovered
 * row — also when the cursor is synced from the other lead-time chart.
 */
const ActiveCaptions = ({
  captions,
  data,
  isTooltipActive,
  activeLabel,
  activeTooltipIndex,
  xAxisMap,
  yAxisMap,
  offset,
}: CustomizedInjectedProps & {
  captions: Captions;
  // The chart's rows, injected by recharts.
  data?: readonly Record<string, unknown>[];
}) => {
  const theme = useTheme();
  const xScale = xAxisMap?.[0]?.scale;
  const yScale = yAxisMap?.[0]?.scale;
  const row =
    isTooltipActive && activeTooltipIndex != null
      ? data?.[activeTooltipIndex]
      : undefined;
  if (!row || activeLabel == null || !xScale || !yScale || !offset) return null;

  const placed = captions.items
    .flatMap((item) => {
      const value = row[item.dataKey];
      return typeof value === "number"
        ? [{ item, value, y: yScale(value) }]
        : [];
    })
    .sort((a, b) => a.y - b.y);
  if (!placed.length) return null;

  const half = CAPTION_FONT_SIZE / 2;
  const ys = spreadPositions(
    placed.map((p) => p.y),
    MIN_SPACING,
    offset.top + half,
    offset.top + offset.height - half
  );
  const x = xScale(activeLabel) - DOT_CLEARANCE;

  return (
    <g pointerEvents="none">
      {placed.map((p, i) => (
        <text
          key={p.item.dataKey}
          className={
            p.item.catId != null ? catClassName(p.item.catId) : undefined
          }
          x={x}
          y={ys[i] ?? p.y}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={CAPTION_FONT_SIZE}
          fontWeight={600}
          fill={theme.vars.palette.text.primary}
          stroke={theme.vars.palette.background.paper}
          strokeWidth={3}
          style={{ paintOrder: "stroke" }}
        >
          {`${p.item.label}: ${captions.format(p.value)}`}
        </text>
      ))}
    </g>
  );
};

export default ActiveCaptions;
